/**
 * Copyright (c), Vector Engineering LLC
 * All rights reserved.
 * 
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 * Commercial use requires explicit written permission.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, Paper, Typography, Button, Box, Chip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TimerIcon from '@mui/icons-material/Timer';
import { estimateScriptDuration, formatDuration } from '../utils/durationEstimator';
import EditPromptDialog from '../components/ScriptForm/EditPromptDialog';
import GenerationProgress from '../components/GenerationProgress';
import { useGeneration } from '../contexts/GenerationContext';
import apiService from '../services/api';
import generationService from '../services/generationService';
import { FormData } from '../components/ScriptForm/types';
import { socialTrendsService } from '../services/socialTrendsService';

// Form storage key for session persistence
const FORM_STORAGE_KEY = 'dramazNow_formData';
const PROJECT_STORAGE_KEY = 'dramazNow_currentProject';

const CreateScriptPage: React.FC = () => {
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [screenplayParams, setScreenplayParams] = useState<any>(null);

  // Load screenplay parameters and initialize project
  useEffect(() => {
    const initializeProject = async () => {
      try {
        setIsLoading(true);
        
        // Load screenplay-prompt.md content
        const response = await fetch('/screenplay-prompt.md');
        const screenplayContent = await response.text();
        setScreenplayParams(screenplayContent);
        
        // Create new project
        const newProject = await apiService.createProject({
          title: 'Auto-Generated Script',
          last_step: 0,
          characters: []
        });
        setProjectId(newProject.id);
        sessionStorage.setItem(PROJECT_STORAGE_KEY, newProject.id.toString());
        
      } catch (error) {
        console.error('Failed to initialize project:', error);
        setGenerationError('An unexpected error occurred. Please try refreshing the page.');
      } finally {
        setIsLoading(false);
      }
    };

    initializeProject();
  }, []);

  // Access generation context
  const { 
    isGenerating: isGeneratingContext, 
    currentStep,
    totalScenes,
    error: generationContextError,
    startGeneration,
    updateStep,
    endGeneration,
    setSceneCount
  } = useGeneration();

  // Generate optimized form data using social trends service
  const generateOptimizedFormData = async (): Promise<FormData> => {
    try {
      // Get optimized elements from social trends service
      const { theme, plotElements, characterTypes } = await socialTrendsService.getOptimizedElements();

      // Generate form data with optimized selections
      const formData: FormData = {
        basicInfo: {
          title: `Teen Drama: ${theme.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}`,
          theme: 'Teen Drama',
          setting: 'Contemporary High School'
        },
        characters: [
          {
            name: characterTypes[0].split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
            age: 16,
            description: 'Lead character with a compelling personal journey',
            voiceType: 'teen_female_casual' as 'teen_female_casual'
          },
          {
            name: characterTypes[1].split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
            age: 16,
            description: 'Supporting character with contrasting perspective',
            voiceType: 'teen_female_shy' as 'teen_female_shy'
          }
        ],
        context: {
          situation: screenplayParams
        },
        settings: {
          wordCount: 2500,
          duration: 15
        },
        selectedModels: [
          {
            characterId: characterTypes[0].split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
            modelPath: '/static/gltf_characters/Character1/Character1.glb',
            voiceType: 'teen_female_casual' as 'teen_female_casual'
          },
          {
            characterId: characterTypes[1].split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
            modelPath: '/static/gltf_characters/Character2/Character2.glb',
            voiceType: 'teen_female_shy' as 'teen_female_shy'
          }
        ],
        tropes: {
          selectedTropes: [theme, ...plotElements]
        }
      };

      return formData;
    } catch (error) {
      console.error('Failed to generate optimized form data:', error);
      throw new Error('Failed to generate optimized content');
    }
  };

  // Generate the prompt text from form data
  const generatePromptText = (formData: FormData) => {
    // Create tropes string for context
    const tropesString = formData.tropes.selectedTropes.length > 0
      ? formData.tropes.selectedTropes.map(trope => `"${trope}"`).join(', ').replace(/,([^,]*)$/, ' and$1')
      : 'teen drama elements';

    // Create enhanced context with tropes
    const enhancedContext = `Please generate detailed teen drama scenes focusing on ${tropesString}. The target word count for spoken dialogue only (excluding scene descriptions, character cues, and stage directions) will be provided separately. Create approximately 5-7 scenes following this structure:

ACT 1 (1-2 scenes):
- Introduce characters, setting, and establish the initial situation
- End with an inciting incident that disrupts the status quo
- Introduce ${formData.tropes.selectedTropes.length > 0 ? 'the ' + formData.tropes.selectedTropes[0] + ' trope' : 'key story elements'} early on

ACT 2 (3-4 scenes):
- Show escalating conflict and character development
- Include a midpoint scene that raises the stakes
- Develop and explore ${tropesString} throughout these scenes
- End with a crisis moment where characters face their biggest challenge

ACT 3 (1-2 scenes):
- Resolve the conflict
- Bring closure to ${formData.tropes.selectedTropes.length > 0 ? 'all trope storylines' : 'all storylines'} in a satisfying way
- Show character growth and deliver the educational message

For each individual scene:
1. Provide a specific location within the main setting consistent with the theme
2. Include clear conflict or tension that drives the scene forward along the storyline
3. End with a hook leading to the next scene
4. Include specific camera angles and close-ups for emotional moments
5. Add detailed facial expressions and body language cues for 3D animation`;

    const scriptPrompt = {
      title: formData.basicInfo.title,
      theme: formData.basicInfo.theme,
      setting: formData.basicInfo.setting,
      characters: formData.characters.map(char => ({
        name: char.name,
        age: char.age,
        description: char.description,
        modelPath: formData.selectedModels?.find(m => m.characterId === char.name)?.modelPath,
        voiceType: formData.selectedModels?.find(m => m.characterId === char.name)?.voiceType
      })),
      context: enhancedContext,
      target_audience: "Teenagers",
      target_word_count: formData.settings.wordCount || 1000,
      target_word_count_note: "The target word count applies ONLY to spoken dialogue, excluding scene descriptions, character cues, and stage directions.",
      duration: formData.settings.duration || 15,
      animation_requirements: {
        include_facial_expressions: true,
        include_body_language: true,
        include_camera_angles: true,
        include_voice_modulation: true
      },
      tropes: formData.tropes.selectedTropes,
      timestamp: Date.now()
    };

    return JSON.stringify(scriptPrompt, null, 2);
  };

  // Handle script generation
  const handleGenerateScript = async () => {
    setIsGenerating(true);
    setGenerationError(null);
    startGeneration();
    
    try {
      // Generate optimized form data
      const formData = await generateOptimizedFormData();
      
      // Generate and set prompt
      const promptText = generatePromptText(formData);
      setCurrentPrompt(promptText);
      
      // Parse the prompt into an object
      const scriptPrompt = JSON.parse(promptText);
      if (projectId) {
        scriptPrompt.project_id = projectId;
      }

      // Set scene count based on target duration
      const estimatedSceneCount = Math.min(7, Math.max(5, Math.ceil(formData.settings.duration / 3)));
      setSceneCount(estimatedSceneCount);
      
      // Generate script
      const scriptId = await generationService.startGeneration(
        scriptPrompt,
        (step, progress, details) => {
          console.log(`Generation progress: ${step} (${progress}%)`);
          updateStep(step);
          
          if (details?.sceneCount) {
            setSceneCount(details.sceneCount);
          }
        }
      );
      
      // Update project status
      if (projectId) {
        await apiService.updateProject(projectId, {
          script_id: typeof scriptId === 'string' ? parseInt(scriptId.replace('script-', ''), 10) : scriptId,
          is_completed: true
        });
      }

      // Navigate to script view
      updateStep('complete');
      setTimeout(() => {
        navigate(`/scripts/${scriptId}`);
      }, 2000);
      
    } catch (error: unknown) {
      console.error('Failed to generate script:', error);
      const errorMessage = error instanceof Error 
        ? `Failed to generate script: ${error.message}` 
        : 'Failed to generate script. Please try again.';
      
      setGenerationError(errorMessage);
      endGeneration(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle navigation back to projects list
  const handleBackToProjects = () => {
    navigate('/scripts');
  };

  if (isLoading) {
    return (
      <Paper className="p-6 mb-8 flex justify-center items-center" style={{ minHeight: "400px" }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 3 }}>Loading project data...</Typography>
      </Paper>
    );
  }

  return (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBackToProjects}
          sx={{ mr: 2 }}
        >
          Back to Projects
        </Button>
        
        <Typography variant="h4" component="h1">
          Create New Script {projectId ? `#${projectId}` : ''}
        </Typography>
      </Box>
      
      {/* Generation progress component */}
      <GenerationProgress 
        currentStep={currentStep}
        totalScenes={totalScenes}
        error={generationError || generationContextError}
        isGenerating={isGenerating}
      />
      
      {generationError && !isGenerating && (
        <Paper className="p-4 mb-4" style={{ background: '#fff0f0' }}>
          <Typography variant="body1" color="error">
            {generationError}
          </Typography>
        </Paper>
      )}

      {/* Auto-generation button */}
      <Paper className="p-6 mb-8">
        <Typography variant="h6" gutterBottom>
          Generate an Optimized Teen Drama Script
        </Typography>
        <Typography variant="body1" paragraph>
          Using social media trend analysis to create engaging content with maximum impact.
        </Typography>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Chip
            icon={<TimerIcon />}
            label={`Estimated Duration: ${formatDuration(estimateScriptDuration(2500, 5))}`}
            color="primary"
            variant="outlined"
          />
        </Box>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleGenerateScript}
          disabled={isGenerating}
        >
          {isGenerating ? 'Generating...' : 'Generate Script'}
        </Button>
      </Paper>

      <EditPromptDialog
        open={showPromptDialog}
        onClose={() => setShowPromptDialog(false)}
        onBack={() => setShowPromptDialog(false)}
        onGenerate={handleGenerateScript}
        prompt={currentPrompt}
        onPromptChange={setCurrentPrompt}
      />
    </div>
  );
};

export default CreateScriptPage;
